import type { AnyReport } from '../comp_reusable/report_page_universal/stores/baseReportStore'
import { ArtifactPolymorphic } from '@/api/generated/models'

// Dummy type for BaseModel intersection
export interface DummyModelExtensions {
  // Placeholder properties for potential model extensions.
  //When Optimize is finished we can replace the DummyModelExtensions with the real Optimize Model type

  // All fields from Artifact interface as placeholders
  readonly id?: number
  name?: string
  slug?: string
  note?: string | null
  timeframe?: any // Using any to avoid importing all the nested types
  parameters?: any
  readonly project_solution?: any
  project_solution_id?: number
  run_at?: string
  fit_score?: string
  health_score?: string
  readonly created_by?: any
  readonly dependent_driver?: any
  dependent_driver_id?: number
  readonly independent_drivers?: readonly any[]
  readonly client?: any
  client_id?: number
  harmonic_features?: any
  model_object?: string | null
  readonly created_at?: string
  readonly updated_at?: string
}

// BaseModel type as intersection of Artifact and dummy extensions
export type BaseModel = ArtifactPolymorphic & DummyModelExtensions

/**
 * Report operational modes
 * These constants define the different ways the report component can operate:
 *
 * NEW_MODEL: Creating a report for a newly generated model
 * VIEW_REPORT: Viewing an existing report (read mode)
 * RERUN_MODEL: Creating a new report from a rerun of an existing model
 * EDIT_MODEL: Editing an existing model and updating its report
 */
export const ROUTED_REPORT_MODES = {
  NEW_MODEL: 'NEW_MODEL',
  VIEW_REPORT: 'VIEW_REPORT',
  RERUN_MODEL: 'RERUN_MODEL',
  EDIT_MODEL: 'EDIT_MODEL',
} as const

export type RoutedReportMode =
  (typeof ROUTED_REPORT_MODES)[keyof typeof ROUTED_REPORT_MODES]

// Interface for route parameters - shared across report types
export interface RouteParams extends Record<string, string | undefined> {
  reportId?: string
  solution?: string
}

// Shared types for report data structures

export interface ProjectClient {
  id?: number
  name?: string
  logo?: string
}

export interface Project {
  id?: number
  name?: string
  logo?: string
  client?: ProjectClient
}

export interface Features {
  ai?: boolean
}

export interface ProjectSolution {
  id?: number
  project?: Project
  features?: Features
  ai_settings?: Record<string, any>
}

export interface Timeframe {
  from: string
  to: string
}

export interface NumberOfDrivers {
  own: number
  external: number
}

export interface ReportTemplate {
  id: number
  name: string
  items?: ReportItem[]
}

export interface ReportItem {
  id: number
  name: string
  slug: string
  description?: string
}

// Location state interface for routing
export interface RouteTowardsReportPackage {
  data?: AnyReport
  origin?: string
  mode?: RoutedReportMode
  modelData?: BaseModel
  oldModelData?: BaseModel
  // IDs for replace operation in EDIT_MODEL (explore mode)
  oldArtifactId?: number
  newArtifactId?: number
}

// Extended Model Data interface for comprehensive typing
export interface ModelData {
  id?: number
  name?: string
  slug?: string
  note?: string
  timeframe?: Record<string, any>
  parameters?: Record<string, any>
  project_solution?: ProjectSolution
  project_solution_id?: number
  run_at?: string
  fit_score?: string | number
  health_score?: string | number
  created_by?: UserMinimal
  dependent_driver?: Dataset
  dependent_driver_id?: number
  independent_drivers?: DatasetMinimal[]
  client?: ProjectClient
  client_id?: number
  created_at?: string
  updated_at?: string
  artifact_id?: number
}

export interface UserMinimal {
  id: number
  name: string
}

export interface Dataset {
  id: number
  name: string
}

export interface DatasetMinimal {
  id: number
  name: string
}
